/**
 * Application shell -- route and wrapper.
 *
 * This is the first slice of SDD-001: the URL structure, the frame that
 * survives navigation, and what happens on the way into a screen. Every screen
 * it routes to is a placeholder that says it is one.
 *
 * Two things here are load-bearing:
 *
 * 1. `<main>` is outside `<Router>`. It is the skip link's target and the one
 *    landmark on the page (SHELL-076), and SHELL-078 requires it to be the same
 *    element after a navigation. Moving it inside a screen would destroy and
 *    rebuild it on every route change, which loses focus to `document.body` and
 *    breaks the skip link partway through a session.
 * 2. No game is imported. Not here, not anywhere in `src/shell/`. Games arrive
 *    through a dynamic `import()` when the loader exists; `scripts/check-budget.mjs`
 *    is what notices if that stops being true, because a static import leaves
 *    the app working perfectly. See ADR-0009.
 */

import { LocationProvider, Route, Router } from 'preact-iso';
import type { ComponentChildren } from 'preact';

import { ErrorBoundary } from '@shell/ErrorBoundary';
import { enterScreen } from '@shell/screen-transition';
import { LevelSelect } from '@shell/screens/LevelSelect';
import { NotFound } from '@shell/screens/NotFound';
import { Picker } from '@shell/screens/Picker';
import { Play } from '@shell/screens/Play';
import { RouteError } from '@shell/screens/RouteError';
import { Settings } from '@shell/screens/Settings';
import { UpdateNotice } from '@shell/UpdateNotice';

/**
 * SHELL-006. One boundary per route, not one around `<Router>`: preact-iso
 * unmounts the previously matched route's tree on every navigation, so a
 * fresh, untripped boundary arrives with each new screen for free, and a
 * screen that threw does not have to be explicitly "reset" to recover. A
 * boundary wrapped around `<Router>` itself would trip once and never let a
 * later, working navigation back in.
 */
function routeBoundary(children: ComponentChildren) {
  return <ErrorBoundary fallback={() => <RouteError />}>{children}</ErrorBoundary>;
}

const GuardedPicker = () => routeBoundary(<Picker />);
const GuardedLevelSelect = (props: { gameId: string }) => routeBoundary(<LevelSelect {...props} />);
const GuardedPlay = (props: { gameId: string; levelId: string }) =>
  routeBoundary(<Play {...props} />);
const GuardedSettings = () => routeBoundary(<Settings />);
const GuardedNotFound = () => routeBoundary(<NotFound />);

export function App() {
  return (
    <LocationProvider>
      <UpdateNotice />
      {/*
        tabIndex=-1 so the skip link actually moves focus here; without it most
        browsers scroll but leave focus on the link. WCAG 2.4.1.
      */}
      <main id="main" class="shell" tabIndex={-1}>
        {/*
          Route order matters: preact-iso takes the first child whose pattern
          matches and falls back to the one marked `default`. The default route
          must therefore be last -- its pattern is undefined, which matches `/`.
        */}
        <Router onRouteChange={enterScreen}>
          <Route path="/" component={GuardedPicker} />
          <Route path="/game/:gameId" component={GuardedLevelSelect} />
          <Route path="/play/:gameId/:levelId" component={GuardedPlay} />
          <Route path="/settings" component={GuardedSettings} />
          <Route default component={GuardedNotFound} />
        </Router>
      </main>
    </LocationProvider>
  );
}
